import maplibregl from "maplibre-gl";
import {
  Protocol,
} from "pmtiles";

const PORTUGAL_ROAD_SOURCE_ID =
  "loadevo-portugal-road-network";

const PORTUGAL_ROAD_SOURCE_LAYER =
  "road_shield_anchors";

const PORTUGAL_ROAD_PMTILES_PATH =
  "/maps/road-network/portugal-road-network.pmtiles?v=12";

const EUROPE_MOTORWAY_SOURCE_ID =
  "loadevo-europe-motorway-lines";

const EUROPE_MOTORWAY_SOURCE_LAYER =
  "road_lines";

const EUROPE_MOTORWAY_PMTILES_PATH =
  "/maps/road-network/europe-motorway-lines-v3.pmtiles?v=3";

const PORTUGAL_ROAD_SHIELD_LAYER_ID =
  "loadevo-map-portugal-road-shields";

const EUROPE_ROAD_SHIELD_LAYER_ID =
  "loadevo-map-europe-road-shields";

const EUROPE_MOTORWAY_CASING_LAYER_ID =
  "loadevo-map-europe-motorways-casing";

const EUROPE_MOTORWAY_LINE_LAYER_ID =
  "loadevo-map-europe-motorways";

const ROAD_SHIELD_MOTORWAY_ICON_ID =
  "loadevo-map-road-shield-motorway";

const ROAD_SHIELD_EUROPEAN_ICON_ID =
  "loadevo-map-road-shield-european";

const ROAD_SHIELD_NATIONAL_ICON_ID =
  "loadevo-map-road-shield-national";

const BASE_ROAD_SHIELD_LAYER_IDS = [
  "highway-shield-non-us",
  "highway-shield-us-interstate",
  "road_shield_us",
];
const PM_TILES_PROTOCOL_GLOBAL_KEY =
  "__loadevoPmtilesProtocol";

function ensurePmtilesProtocol() {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  if (
    window[
      PM_TILES_PROTOCOL_GLOBAL_KEY
    ]
  ) {
    return;
  }

  const protocol =
    new Protocol();

  maplibregl.addProtocol(
    "pmtiles",
    protocol.tile
  );

  window[
    PM_TILES_PROTOCOL_GLOBAL_KEY
  ] =
    protocol;
}

function drawRoundedRectangle(
  context,
  x,
  y,
  width,
  height,
  radius
) {
  const safeRadius =
    Math.min(
      radius,
      width / 2,
      height / 2
    );

  context.beginPath();

  context.moveTo(
    x + safeRadius,
    y
  );

  context.lineTo(
    x + width - safeRadius,
    y
  );

  context.quadraticCurveTo(
    x + width,
    y,
    x + width,
    y + safeRadius
  );

  context.lineTo(
    x + width,
    y + height - safeRadius
  );

  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - safeRadius,
    y + height
  );

  context.lineTo(
    x + safeRadius,
    y + height
  );

  context.quadraticCurveTo(
    x,
    y + height,
    x,
    y + height - safeRadius
  );

  context.lineTo(
    x,
    y + safeRadius
  );

  context.quadraticCurveTo(
    x,
    y,
    x + safeRadius,
    y
  );

  context.closePath();
}

function createStretchableRoadShieldImage({
  backgroundColor,
  borderColor,
}) {
  const pixelRatio =
    2;

  const logicalWidth =
    34;

  const logicalHeight =
    20;

  const width =
    logicalWidth *
    pixelRatio;

  const height =
    logicalHeight *
    pixelRatio;

  const canvas =
    document.createElement(
      "canvas"
    );

  canvas.width =
    width;

  canvas.height =
    height;

  const context =
    canvas.getContext(
      "2d"
    );

  if (!context) {
    return null;
  }

  context.clearRect(
    0,
    0,
    width,
    height
  );

  context.fillStyle =
    backgroundColor;

  context.strokeStyle =
    borderColor;

  context.lineWidth =
    pixelRatio;

  drawRoundedRectangle(
    context,
    pixelRatio,
    pixelRatio,
    width -
      2 * pixelRatio,
    height -
      2 * pixelRatio,
    3 * pixelRatio
  );

  context.fill();
  context.stroke();

  return {
    image:
      context.getImageData(
        0,
        0,
        width,
        height
      ),

    options: {
      pixelRatio,

      content: [
        5 * pixelRatio,
        3 * pixelRatio,
        width -
          5 * pixelRatio,
        height -
          3 * pixelRatio,
      ],

      stretchX: [
        [
          9 * pixelRatio,
          width -
            9 * pixelRatio,
        ],
      ],

      stretchY: [
        [
          6 * pixelRatio,
          height -
            6 * pixelRatio,
        ],
      ],
    },
  };
}

function ensureRoadShieldImage(
  map,
  imageId,
  configuration
) {
  if (
    map.hasImage(
      imageId
    )
  ) {
    return;
  }

  const shield =
    createStretchableRoadShieldImage(
      configuration
    );

  if (!shield) {
    return;
  }

  map.addImage(
    imageId,
    shield.image,
    shield.options
  );
}

function ensureRoadShieldImages(
  map
) {
  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_MOTORWAY_ICON_ID,
    {
      backgroundColor:
        "#1f6fb2",

      borderColor:
        "#ffffff",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_EUROPEAN_ICON_ID,
    {
      backgroundColor:
        "#3f8f3f",

      borderColor:
        "#ffffff",
    }
  );

  ensureRoadShieldImage(
    map,
    ROAD_SHIELD_NATIONAL_ICON_ID,
    {
      backgroundColor:
        "#ffffff",

      borderColor:
        "#475569",
    }
  );
}

function hideBaseRoadShieldLayers(
  map
) {
  BASE_ROAD_SHIELD_LAYER_IDS.forEach(
    (
      layerId
    ) => {
      if (
        !map.getLayer(
          layerId
        )
      ) {
        return;
      }

      try {
        map.setLayoutProperty(
          layerId,
          "visibility",
          "none"
        );
      } catch {}
    }
  );
}

function buildPortugalSourceUrl() {
  const absoluteUrl =
    new URL(
      PORTUGAL_ROAD_PMTILES_PATH,
      window.location.origin
    ).toString();

  return `pmtiles://${absoluteUrl}`;
}

function ensurePortugalRoadSource(
  map
) {
  if (
    map.getSource(
      PORTUGAL_ROAD_SOURCE_ID
    )
  ) {
    return;
  }

  map.addSource(
    PORTUGAL_ROAD_SOURCE_ID,
    {
      type:
        "vector",

      url:
        buildPortugalSourceUrl(),

      attribution:
        "© OpenStreetMap contributors",
    }
  );
}

function buildEuropeMotorwaySourceUrl() {
  const absoluteUrl =
    new URL(
      EUROPE_MOTORWAY_PMTILES_PATH,
      window.location.origin
    ).toString();

  return `pmtiles://${absoluteUrl}`;
}

function ensureEuropeMotorwaySource(
  map
) {
  if (
    map.getSource(
      EUROPE_MOTORWAY_SOURCE_ID
    )
  ) {
    return;
  }

  map.addSource(
    EUROPE_MOTORWAY_SOURCE_ID,
    {
      type:
        "vector",

      url:
        buildEuropeMotorwaySourceUrl(),

      attribution:
        "© OpenStreetMap contributors",
    }
  );
}

function ensurePortugalRoadShields(
  map
) {
  ensurePortugalRoadSource(
    map
  );

  if (
    map.getLayer(
      PORTUGAL_ROAD_SHIELD_LAYER_ID
    )
  ) {
    return;
  }

  map.addLayer({
    id:
      PORTUGAL_ROAD_SHIELD_LAYER_ID,

    type:
      "symbol",

    source:
      PORTUGAL_ROAD_SOURCE_ID,

    "source-layer":
      PORTUGAL_ROAD_SOURCE_LAYER,

    minzoom:
      5,

    maxzoom:
      16,

    filter: [
      "in",

      [
        "get",
        "road_class",
      ],

      [
        "literal",
        [
          "motorway",
          "european",
          "principal",
          "national",
          "regional",
          "municipal",
        ],
      ],
    ],

    layout: {
      "symbol-placement":
        "point",

      "symbol-z-order":
        "source",

      "symbol-sort-key": [
        "coalesce",
        [
          "to-number",
          [
            "get",
            "priority",
          ],
        ],
        99,
      ],

      "symbol-avoid-edges":
        true,

      "text-field": [
        "to-string",
        [
          "get",
          "ref",
        ],
      ],

      "text-font": [
        "Noto Sans Bold",
      ],

      "text-size": [
        "interpolate",
        [
          "linear",
        ],
        [
          "zoom",
        ],

        5,
        8.5,

        7,
        9,

        9,
        10,

        12,
        10.5,

        15,
        11,
      ],

      "text-padding":
        2,

      "text-rotation-alignment":
        "viewport",

      "text-pitch-alignment":
        "viewport",

      "text-keep-upright":
        true,

      "text-allow-overlap":
        false,

      "text-ignore-placement":
        false,

      "icon-image": [
        "match",

        [
          "get",
          "road_class",
        ],

        "motorway",
        ROAD_SHIELD_MOTORWAY_ICON_ID,

        "european",
        ROAD_SHIELD_EUROPEAN_ICON_ID,

        "principal",
        ROAD_SHIELD_MOTORWAY_ICON_ID,

        ROAD_SHIELD_NATIONAL_ICON_ID,
      ],

      "icon-text-fit":
        "both",

      "icon-text-fit-padding": [
        1.5,
        4,
        1.5,
        4,
      ],

      "icon-padding": [
        "interpolate",
        [
          "linear",
        ],
        [
          "zoom",
        ],

        5,
        24,

        7,
        18,

        9,
        12,

        11,
        8,

        14,
        4,
      ],

      "icon-rotation-alignment":
        "viewport",

      "icon-pitch-alignment":
        "viewport",

      "icon-allow-overlap":
        false,

      "icon-ignore-placement":
        false,
    },

    paint: {
      "text-color": [
        "match",

        [
          "get",
          "road_class",
        ],

        "motorway",
        "#ffffff",

        "european",
        "#ffffff",

        "principal",
        "#ffffff",

        "#334155",
      ],

      "text-halo-width":
        0,
    },
  });
}

function getTransportationSourceId(
  map
) {
  const style =
    map?.getStyle?.();

  const layers =
    Array.isArray(
      style?.layers
    )
      ? style.layers
      : [];

  const transportationLayer =
    layers.find(
      (
        layer
      ) =>
        layer?.source &&
        layer[
          "source-layer"
        ] ===
          "transportation"
    );

  return (
    transportationLayer
      ?.source ||
    null
  );
}

function getTransportationNameSourceId(
  map
) {
  const style =
    map?.getStyle?.();

  const layers =
    Array.isArray(
      style?.layers
    )
      ? style.layers
      : [];

  const transportationLayer =
    layers.find(
      (
        layer
      ) =>
        layer?.source &&
        layer[
          "source-layer"
        ] ===
          "transportation_name"
    );

  return (
    transportationLayer
      ?.source ||
    null
  );
}

function getFirstMapSymbolLayerId(
  map
) {
  const style =
    map?.getStyle?.();

  const layers =
    Array.isArray(
      style?.layers
    )
      ? style.layers
      : [];

  return (
    layers.find(
      (
        layer
      ) =>
        layer?.type ===
        "symbol"
    )?.id ||
    undefined
  );
}

function ensureEuropeMotorwayLines(
  map
) {
  ensureEuropeMotorwaySource(
    map
  );

  const beforeId =
    getFirstMapSymbolLayerId(
      map
    );

  if (
    !map.getLayer(
      EUROPE_MOTORWAY_CASING_LAYER_ID
    )
  ) {
    map.addLayer(
      {
        id:
          EUROPE_MOTORWAY_CASING_LAYER_ID,

        type:
          "line",

        source:
          EUROPE_MOTORWAY_SOURCE_ID,

        "source-layer":
          EUROPE_MOTORWAY_SOURCE_LAYER,

        minzoom:
          5,

        filter: [
          "in",

          [
            "get",
            "highway",
          ],

          [
            "literal",
            [
              "motorway",
              "motorway_link",
            ],
          ],
        ],

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color":
            "#fff4df",

          "line-width": [
            "interpolate",

            [
              "linear",
            ],

            [
              "zoom",
            ],

            5,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              2.8,

              3.8,
            ],

            14,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              5.6,

              7.2,
            ],
          ],

          "line-opacity":
            0.92,
        },
      },
      beforeId
    );
  }

  if (
    !map.getLayer(
      EUROPE_MOTORWAY_LINE_LAYER_ID
    )
  ) {
    map.addLayer(
      {
        id:
          EUROPE_MOTORWAY_LINE_LAYER_ID,

        type:
          "line",

        source:
          EUROPE_MOTORWAY_SOURCE_ID,

        "source-layer":
          EUROPE_MOTORWAY_SOURCE_LAYER,

        minzoom:
          5,

        filter: [
          "in",

          [
            "get",
            "highway",
          ],

          [
            "literal",
            [
              "motorway",
              "motorway_link",
            ],
          ],
        ],

        layout: {
          "line-cap":
            "round",

          "line-join":
            "round",
        },

        paint: {
          "line-color":
            "#e69532",

          "line-width": [
            "interpolate",

            [
              "linear",
            ],

            [
              "zoom",
            ],

            5,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              1.6,

              2.4,
            ],

            14,

            [
              "match",

              [
                "get",
                "highway",
              ],

              "motorway_link",
              3.8,

              5.4,
            ],
          ],

          "line-opacity":
            0.92,
        },
      },
      beforeId
    );
  }
}

function ensureEuropeRoadShields(
  map
) {
  if (
    !map ||
    map.getLayer(
      EUROPE_ROAD_SHIELD_LAYER_ID
    )
  ) {
    return;
  }

  const sourceId =
    getTransportationNameSourceId(
      map
    );

  if (!sourceId) {
    console.warn(
      "[roadReferenceOverlay] transportation_name source not found."
    );

    return;
  }

  ensureRoadShieldImages(
    map
  );

  map.addLayer({
    id:
      EUROPE_ROAD_SHIELD_LAYER_ID,

    type:
      "symbol",

    source:
      sourceId,

    "source-layer":
      "transportation_name",

    minzoom:
      5,

    filter: [
      "all",

      [
        "any",

        [
          "has",
          "ref",
        ],

        [
          "has",
          "route_1_ref",
        ],
      ],

      [
        "==",

        [
          "geometry-type",
        ],

        "LineString",
      ],

      [
        "match",

        [
          "get",
          "network",
        ],

        [
          "us-highway",
          "us-interstate",
          "us-state",
        ],

        false,

        true,
      ],
    ],

    layout: {
      "symbol-placement":
        "line",

      "symbol-spacing":
        145,

      "symbol-avoid-edges":
        true,

      "symbol-sort-key": [
        "match",

        [
          "get",
          "class",
        ],

        "motorway",
        0,

        "trunk",
        1,

        "primary",
        2,

        "secondary",
        3,

        4,
      ],

      "text-field": [
        "to-string",

        [
          "coalesce",

          [
            "get",
            "ref",
          ],

          [
            "get",
            "route_1_ref",
          ],

          "",
        ],
      ],

      "text-font": [
        "Noto Sans Regular",
      ],

      "text-size": [
        "interpolate",

        [
          "linear",
        ],

        [
          "zoom",
        ],

        6,
        9,

        8,
        10,

        10,
        11,

        12,
        11.5,

        15,
        12,
      ],

      "text-padding":
        2,

      "text-rotation-alignment":
        "viewport",

      "text-pitch-alignment":
        "viewport",

      "text-keep-upright":
        true,

      "text-allow-overlap":
        false,

      "text-ignore-placement":
        false,

      "icon-image": [
        "case",

        /*
         * European E-routes stay green.
         */
        [
          "==",

          [
            "slice",

            [
              "upcase",

              [
                "to-string",

                [
                  "coalesce",

                  [
                    "get",
                    "ref",
                  ],

                  [
                    "get",
                    "route_1_ref",
                  ],

                  "",
                ],
              ],
            ],

            0,
            1,
          ],

          "E",
        ],

        ROAD_SHIELD_EUROPEAN_ICON_ID,

        /*
         * Every other European road reference
         * uses the same blue LOADEvo shield.
         *
         * Do not derive shield colour from the
         * OpenMapTiles road class because the same
         * numbered road can change class between
         * individual segments.
         */
        ROAD_SHIELD_MOTORWAY_ICON_ID,
      ],

      "icon-text-fit":
        "both",

      "icon-text-fit-padding": [
        1.5,
        4,
        1.5,
        4,
      ],

      "icon-padding":
        5,

      "icon-rotation-alignment":
        "viewport",

      "icon-pitch-alignment":
        "viewport",

      "icon-allow-overlap":
        false,

      "icon-ignore-placement":
        false,
    },

    paint: {
      "text-color":
        "#ffffff",

      "text-halo-width":
        0,
    },
  });
}

export function ensureRoadReferenceOverlay(
  map
) {
  if (
    !map ||
    !map.getStyle?.()
  ) {
    return;
  }

  ensurePmtilesProtocol();

  hideBaseRoadShieldLayers(
    map
  );

  /*
   * One motorway presentation across Europe.
   *
   * Portugal and the rest of Europe now use
   * the exact same:
   *
   * - min zoom
   * - casing width
   * - motorway width
   * - motorway colour
   */
  ensureEuropeMotorwayLines(
    map
  );

  ensureRoadShieldImages(
    map
  );

  /*
   * Portugal keeps its detailed local-road
   * references:
   *
   * - principal
   * - national
   * - regional
   * - municipal
   */
  ensurePortugalRoadShields(
    map
  );

  /*
   * Motorway and European-route references
   * now come from one LOADEvo PMTiles dataset
   * for the whole of Europe, including
   * Portugal.
   *
   * This gives every country the same
   * progressive z5+ shield behaviour.
   */
  ensureEuropeRoadShields(
    map
  );

  /*
   * Keep LOADEvo road-reference shields above
   * route geometry and base-map labels.
   *
   * The generic OpenFreeMap shields remain hidden,
   * matching the Edit Load map.
   */
  try {
    if (
      map.getLayer(
        PORTUGAL_ROAD_SHIELD_LAYER_ID
      ) &&
      map.getLayer(
        EUROPE_ROAD_SHIELD_LAYER_ID
      )
    ) {
      map.moveLayer(
        PORTUGAL_ROAD_SHIELD_LAYER_ID,
        EUROPE_ROAD_SHIELD_LAYER_ID
      );
    } else if (
      map.getLayer(
        PORTUGAL_ROAD_SHIELD_LAYER_ID
      )
    ) {
      map.moveLayer(
        PORTUGAL_ROAD_SHIELD_LAYER_ID
      );
    }

    if (
      map.getLayer(
        EUROPE_ROAD_SHIELD_LAYER_ID
      )
    ) {
      map.moveLayer(
        EUROPE_ROAD_SHIELD_LAYER_ID
      );
    }
  } catch {}
}